import React, { useRef, useState } from 'react';

import {
  type SupportedLanguage,
  changeBrowserLanguage,
  defaultLanguage,
  formatLocaleLabel,
  getCurrentLanguage,
  useTranslation,
} from '@modernloop/i18n';
import { useLocales, useShowLanguageSelector } from '@modernloop/shared/feature-flag';
import { ArrowRightIcon, LanguagesIcon } from '@modernloop/shared/icons';
import { ListItemIcon, ListItemText, Menu, MenuItem, Typography } from '@mui/material';

import { useUpdateEmployeeLocale } from 'src/hooks/api/employee/useUpdateEmployeeLocale';
import useEmployeeId from 'src/hooks/useEmployeeId';

type Props = {
  onSelect: () => void;
};

/** Language preference shown inside the Employee account menu. */
const AccountMenuLanguageItem = ({ onSelect }: Props): JSX.Element | null => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLLIElement>(null);
  const employeeId = useEmployeeId();
  const updateLocale = useUpdateEmployeeLocale(employeeId);
  const showLanguageSelector = useShowLanguageSelector();
  const availableLanguages = useLocales();
  const currentLanguage = (getCurrentLanguage() || defaultLanguage) as SupportedLanguage;

  if (!showLanguageSelector) {
    return null;
  }

  const handleSelectLanguage = async (language: SupportedLanguage) => {
    await changeBrowserLanguage(language);
    updateLocale(language);
    setOpen(false);
    onSelect();
  };

  return (
    <>
      <MenuItem
        ref={anchorRef}
        id="account-menu-language"
        data-testid="account-menu-language"
        onClick={() => setOpen(true)}
      >
        <ListItemIcon>
          <LanguagesIcon />
        </ListItemIcon>
        <ListItemText
          primary={t('common:language.selectLanguage')}
          secondary={formatLocaleLabel(currentLanguage, i18n.language)}
        />
        <ArrowRightIcon fontSize="small" />
      </MenuItem>
      <Menu
        anchorEl={anchorRef.current}
        open={open}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {availableLanguages.map((language) => {
          const isSelected = language === i18n.resolvedLanguage;
          return (
            <MenuItem
              key={language}
              id={`account-menu-language-${language}`}
              data-testid={`account-menu-language-${language}`}
              selected={isSelected}
              onClick={() => handleSelectLanguage(language)}
            >
              <Typography fontWeight={isSelected ? 600 : 400}>{formatLocaleLabel(language, i18n.language)}</Typography>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default AccountMenuLanguageItem;
